import React from 'react';
import TaskCard from './TaskCard';
import './TaskColumn.css';

interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  owner: string | null;
  due_date: string | null;
  task_type: string | null;
  person_id: string | null;
  person_name?: string | null;
  company_id: string | null;
  company_name?: string | null;
  project_id: string | null;
  project_name?: string | null;
  github_issue_url: string;
}

interface TaskColumnProps {
  title: string;
  status: string;
  tasks: Task[];
  onStatusChange: (taskId: string, newStatus: string) => void;
  onEdit: (taskId: string) => void;
  onDrop: (taskId: string, newStatus: string) => void;
  onArchiveAll?: () => void;
  archiving?: boolean;
}

const TaskColumn: React.FC<TaskColumnProps> = ({
  title,
  status,
  tasks,
  onStatusChange,
  onEdit,
  onDrop,
  onArchiveAll,
  archiving = false,
}) => {
  const [dragOver, setDragOver] = React.useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId) {
      onDrop(taskId, status);
    }
  };

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('taskId', taskId);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      className={`task-column ${dragOver ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="task-column-header">
        <div className="task-column-header-left">
          <h3 className="task-column-title">{title}</h3>
          <span className="task-column-count">{tasks.length}</span>
        </div>
        {onArchiveAll && tasks.length > 0 && (
          <button
            className="task-column-archive-btn"
            onClick={onArchiveAll}
            disabled={archiving}
            type="button"
          >
            {archiving ? 'Archiving...' : 'Archive All'}
          </button>
        )}
      </div>
      <div className="task-column-content">
        {tasks.length === 0 ? (
          <div className="task-column-empty">No tasks</div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              draggable
              onDragStart={(e) => handleDragStart(e, task.id)}
            >
              <TaskCard
                task={task}
                onStatusChange={onStatusChange}
                onEdit={onEdit}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TaskColumn;
